import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { FetchStatus } from '../types/common';
import { GitlabGroupStatus } from '../services/gitlabAdminService';

export interface GitlabAdminState {
  groups: GitlabGroupStatus[];
  status: FetchStatus;
  errorMessage: string;
  // Actions par groupe (clé = groupId), pour afficher un spinner par ligne
  groupActionStatus: Record<number, FetchStatus>;
  groupActionError: Record<number, string>;
  // Actions en masse
  bulkActionStatus: FetchStatus;
  bulkActionError: string;
}

const initialState: GitlabAdminState = {
  groups: [],
  status: FetchStatus.INITIAL,
  errorMessage: '',
  groupActionStatus: {},
  groupActionError: {},
  bulkActionStatus: FetchStatus.INITIAL,
  bulkActionError: '',
};

const gitlabAdminSlice = createSlice({
  name: 'gitlabAdmin',
  initialState,
  reducers: {
    fetchGroupsStart(state) {
      state.status = FetchStatus.LOADING;
      state.errorMessage = '';
    },
    fetchGroupsSuccess(state, action: PayloadAction<GitlabGroupStatus[]>) {
      state.status = FetchStatus.SUCCESS;
      state.groups = action.payload;
      state.errorMessage = '';
    },
    fetchGroupsFailure(state, action: PayloadAction<string>) {
      state.status = FetchStatus.ERROR;
      state.errorMessage = action.payload;
    },
    groupActionStart(state, action: PayloadAction<{ groupId: number }>) {
      state.groupActionStatus[action.payload.groupId] = FetchStatus.LOADING;
      state.groupActionError[action.payload.groupId] = '';
    },
    groupActionSuccess(state, action: PayloadAction<{ groupId: number; group?: GitlabGroupStatus }>) {
      state.groupActionStatus[action.payload.groupId] = FetchStatus.SUCCESS;
      if (action.payload.group) {
        const idx = state.groups.findIndex((g) => g.id === action.payload.groupId);
        if (idx !== -1) {
          state.groups[idx] = action.payload.group;
        }
      }
    },
    groupActionFailure(state, action: PayloadAction<{ groupId: number; error: string }>) {
      state.groupActionStatus[action.payload.groupId] = FetchStatus.ERROR;
      state.groupActionError[action.payload.groupId] = action.payload.error;
    },
    bulkActionStart(state) {
      state.bulkActionStatus = FetchStatus.LOADING;
      state.bulkActionError = '';
    },
    bulkActionSuccess(state) {
      state.bulkActionStatus = FetchStatus.SUCCESS;
    },
    bulkActionFailure(state, action: PayloadAction<string>) {
      state.bulkActionStatus = FetchStatus.ERROR;
      state.bulkActionError = action.payload;
    },
  }
});

export const {
  fetchGroupsStart,
  fetchGroupsSuccess,
  fetchGroupsFailure,
  groupActionStart,
  groupActionSuccess,
  groupActionFailure,
  bulkActionStart,
  bulkActionSuccess,
  bulkActionFailure,
} = gitlabAdminSlice.actions;
export const gitlabAdminReducer = gitlabAdminSlice.reducer;

// Sélecteurs
export const selectGitlabAdminGroups = (state: any) => state.gitlabAdmin?.groups;
export const selectGitlabAdminStatus = (state: any) => state.gitlabAdmin?.status;
export const selectGitlabAdminError = (state: any) => state.gitlabAdmin?.errorMessage;
export const selectGitlabAdminGroupActionStatus = (state: any, groupId: number) => state.gitlabAdmin?.groupActionStatus?.[groupId];
export const selectGitlabAdminBulkActionStatus = (state: any) => state.gitlabAdmin?.bulkActionStatus;
